import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, ArrowRight, ChevronDown, Clock, Quote, Trophy } from "lucide-react";
import { Screen } from "@/components/Screen";
import { PageHeader } from "@/components/PageHeader";
import { ResultBadge, RESULT_META } from "@/components/ResultBadge";
import { PremiumTeaserCard } from "@/components/PremiumTeaserCard";
import { useI18n } from "@/i18n/I18nProvider";
import { getHistory } from "@/store/store";
import { formatDate } from "@/lib/date";

export default function History() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const entries = useMemo(() => getHistory(), []);

  return (
    <Screen testId="history-screen">
      <PageHeader titleKey="history.title" testId="history-header" />

      {entries.length === 0 ? (
        <EmptyState onCta={() => navigate("/today")} />
      ) : (
        <>
          <div
            className="mt-6 space-y-3"
            data-testid="history-list"
          >
            {entries.map((entry) => (
              <HistoryItem key={entry.date} entry={entry} language={language} t={t} />
            ))}
          </div>
          <div className="mt-6">
            <PremiumTeaserCard
              Icon={Archive}
              title={t("premium.historyTeaser")}
              ctaLabel={t("premium.cta.seeIncludes")}
              variant="prominent"
              testId="history-premium-teaser"
            />
          </div>
        </>
      )}
    </Screen>
  );
}

function EmptyState({ onCta }) {
  const { t } = useI18n();
  return (
    <div
      className="dc-card relative overflow-hidden mt-6 p-6 flex flex-col items-center text-center"
      data-testid="history-empty"
    >
      <div
        className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(244,201,93,0.22) 0%, rgba(244,201,93,0) 70%)",
        }}
      />
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center relative"
        style={{
          background:
            "radial-gradient(circle, rgba(244,201,93,0.18) 0%, rgba(244,201,93,0) 70%)",
        }}
      >
        <Clock size={24} className="text-[#F4C95D]" strokeWidth={1.6} />
      </div>
      <p className="mt-4 text-[15px] text-[#CBD5E1] leading-relaxed max-w-[340px]">
        {t("history.empty.message")}
      </p>
      <button
        type="button"
        onClick={onCta}
        data-testid="history-empty-cta"
        className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
        style={{
          background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
          color: "#0F172A",
          letterSpacing: "-0.005em",
        }}
      >
        {t("history.empty.cta")}
        <ArrowRight
          size={16}
          className="transition-transform duration-300 group-hover:translate-x-0.5"
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

function HistoryItem({ entry, language, t }) {
  const [open, setOpen] = useState(false);
  const meta = RESULT_META[entry.result] || RESULT_META.stay;
  const label = t(`result.labels.${entry.result}.title`);

  return (
    <article
      className="dc-card overflow-hidden"
      data-testid={`history-item-${entry.date}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`history-item-toggle-${entry.date}`}
        className="w-full text-left px-4 py-4 flex items-center gap-3 transition-colors duration-200 hover:bg-white/[0.02]"
      >
        <ResultBadge result={entry.result} size={54} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#94A3B8] capitalize">
            {formatDate(entry.date, language)}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <h3
              className="text-[15px] font-medium truncate"
              style={{ color: meta.color, letterSpacing: "-0.01em" }}
            >
              {label}
            </h3>
            <span
              className="text-[11px] text-white/50 tabular-nums flex-shrink-0"
              data-testid={`history-item-balance-${entry.date}`}
            >
              · {entry.balanceScore}/100
            </span>
          </div>
          {entry.dailyWin && !open ? (
            <p className="mt-1 text-[13px] text-[#CBD5E1]/80 italic truncate">
              “{entry.dailyWin}”
            </p>
          ) : null}
        </div>
        <ChevronDown
          size={18}
          strokeWidth={1.7}
          className={`text-white/50 transition-transform duration-300 flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div
          className="px-4 pb-4 pt-1 space-y-3 border-t border-white/[0.05]"
          data-testid={`history-item-details-${entry.date}`}
        >
          <DetailBlock label={t("history.item.recommendationLabel")}>
            <p className="text-[14px] text-[#F8FAFC]/95 leading-relaxed">
              {entry.recommendation}
            </p>
          </DetailBlock>

          {entry.dailyWin ? (
            <DetailBlock label={t("history.item.winLabel")} icon={Trophy}>
              <p className="text-[14px] text-[#F8FAFC]/95 italic leading-relaxed">
                “{entry.dailyWin}”
              </p>
            </DetailBlock>
          ) : null}

          {entry.reflectionNote ? (
            <DetailBlock label={t("history.item.reflectionLabel")} icon={Quote}>
              <p className="text-[14px] text-[#F8FAFC]/95 leading-relaxed whitespace-pre-wrap">
                {entry.reflectionNote}
              </p>
            </DetailBlock>
          ) : null}

          <DetailBlock label={t("history.item.answersLabel")}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px]">
              <AnswerRow
                label={t("history.answers.energy")}
                value={`${entry.energy}/5`}
              />
              <AnswerRow
                label={t("history.answers.mood")}
                value={`${entry.mood}/5`}
              />
              <AnswerRow
                label={t("history.answers.pressure")}
                value={`${entry.pressure}/5`}
              />
              <AnswerRow
                label={t("history.answers.clarity")}
                value={`${entry.clarity}/5`}
              />
              <AnswerRow
                label={t("history.answers.planRealism")}
                value={`${entry.planRealism}/5`}
              />
              <AnswerRow
                label={t("history.answers.need")}
                value={t(`checkin.questions.need.options.${entry.need}`)}
              />
            </dl>
          </DetailBlock>
        </div>
      ) : null}
    </article>
  );
}

function DetailBlock({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon ? (
          <Icon size={12} className="text-[#F4C95D]/80" strokeWidth={1.8} />
        ) : null}
        <p className="text-[10px] uppercase tracking-[0.16em] text-[#E8D8B8]/75">
          {label}
        </p>
      </div>
      {children}
    </div>
  );
}

function AnswerRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/[0.04] pb-1">
      <dt className="text-[#94A3B8]">{label}</dt>
      <dd className="text-[#F8FAFC]/90 font-medium tabular-nums">{value}</dd>
    </div>
  );
}
